import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "../../Utils";
import { fetchVehicleDamageReports } from "../../../api/vehicles/damage";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export const DamageSection = ({ vehicleId, className = "" }) => {
  const { t } = useTranslation();
  const [damageReport, setDamageReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vehicleId) {
      fetchDamageReport();
    }
  }, [vehicleId]);

  const fetchDamageReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchVehicleDamageReports({
        vehicle_id: vehicleId,
        filters: [{ name: "vehicle_id", value: vehicleId }],
        per_page: 1,
        sortBy: "occurred_at",
        sort: "DESC",
      });
      const damageList = response?.data || [];
      setDamageReport(damageList.length > 0 ? damageList[0] : null);
    } catch (err) {
      console.error("Error fetching damage report:", err);
      setError("Failed to load damage report data");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicleId) {
    return null;
  }

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-lg bg-yellow-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-yellow-200 rounded w-1/4"></div>
                <div className="h-3 bg-yellow-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">{t("Error Loading Damage Report")}</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!damageReport) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("Latest Damage Report")}</h3>
                <p className="text-sm text-gray-600">{t("Most recent damage report for this vehicle")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">{t("No damage reports found")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`shadow sm:overflow-hidden sm:rounded-md mt-5 ${className}`}>
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">{t("Latest Damage Report")}</h3>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    damageReport.processed
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {damageReport.processed ? t("Processed") : t("Pending")}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t("Most recent damage report for this vehicle")}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Date Occurred")}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {damageReport.occurred_at
                ? new Date(damageReport.occurred_at).toLocaleDateString()
                : "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Location")}</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center">
              {damageReport.location || "-"}
              {damageReport.location && (
                <CopyToClipboard value={damageReport.location} />
              )}
            </dd>
          </div>
          {damageReport.description && (
            <div className="md:col-span-3">
              <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
              <dd className="mt-1 text-sm text-gray-900">{damageReport.description}</dd>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

